import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppConfigContext } from '@/context/AppConfig';
import { request } from '@/utils/requestHelpers';

export const enum Vote {
  UPVOTE = 'upvote',
  DOWNVOTE = 'downvote',
}

const undoVote = async (
  jsonPath: string,
  permalink: string,
  voteType: Vote
) => {
  const url = `/api/v5/reports/${permalink}`;
  const formData = new FormData();
  formData.append('id', permalink);
  formData.append('undo_report_item', jsonPath);
  formData.append('undo_report_item_action', voteType);

  const options = {
    method: 'PUT',
    body: formData,
  };

  return await request(url, options);
};

const vote = async ({
  jsonPath,
  permalink,
  voteType,
  currentVote,
}: {
  jsonPath: string;
  permalink: string;
  voteType: Vote;
  currentVote: Vote | null;
}) => {
  if (currentVote === voteType) {
    return await undoVote(jsonPath, permalink, voteType);
  }

  const url = `/api/v5/reports/${permalink}`;
  const action = `${voteType}_report_item`;

  const formData = new FormData();
  formData.append('id', permalink);
  formData.append(action, jsonPath);

  const options = {
    method: 'PUT',
    body: formData,
  };

  return await request(url, options);
};

const removeData = async ({
  jsonPath,
  permalink,
}: {
  jsonPath: string;
  permalink: string;
}) => {
  const url = `/api/v5/reports/${permalink}`;

  const parsedData = new URLSearchParams({
    id: permalink,
    hide_report_item: jsonPath,
  });

  const options = {
    method: 'PUT',
    body: parsedData,
  };

  return await request(url, options);
};

const restoreData = async ({
  jsonPaths,
  permalink,
}: {
  jsonPaths: (string | undefined)[];
  permalink: string;
}) => {
  const url = `/api/v5/reports/${permalink}`;
  const formData = new FormData();
  formData.append('id', permalink);
  jsonPaths.forEach((jsonPath, index) => {
    if (jsonPath) {
      return formData.append(`unhide_report_items[${index}]`, jsonPath);
    }
  });
  const options = {
    method: 'PUT',
    body: formData,
  };

  return await request(url, options);
};

export const useVote = (permalink: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { logError } = useAppConfigContext();
  return useMutation({
    mutationFn: vote,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['report', permalink]);
      onSuccess && onSuccess();
    },
    onError: (error) => logError('vote', error as Error),
  });
};

export const useRemoveData = (permalink: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { logError } = useAppConfigContext();
  return useMutation({
    mutationFn: removeData,
    onSuccess: () => {
      onSuccess && onSuccess();
      queryClient.invalidateQueries(['report', permalink]);
    },
    onError: (error) => logError('remove data', error as Error),
  });
};

export const useRestoreData = (permalink: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { logError } = useAppConfigContext();
  return useMutation({
    mutationFn: restoreData,
    onSuccess: () => {
      onSuccess && onSuccess();
      queryClient.invalidateQueries(['report', permalink]);
    },
    onError: (error) => logError('restore data', error as Error),
  });
};
